import {
    discoveryApiRef,
    identityApiRef,
    fetchApiRef,
    type DiscoveryApi,
    type IdentityApi,
    type FetchApi,
} from '@backstage/core-plugin-api';

export class GithubActionsProxyClient /* implements GithubActionsApi */ {
    constructor(
        private readonly opts: {
            discoveryApi: DiscoveryApi;
            identityApi: IdentityApi;
            fetchApi: FetchApi;
        },
    ) { }

    private async baseUrl() {
        // pluginId = 'github-actions' in your backend, so base = /api/github-actions
        return await this.opts.discoveryApi.getBaseUrl('github-actions');
    }

    private async makeRequest(
        endpoint: string,
        options: {
            method?: 'GET' | 'POST';
            responseType?: 'json' | 'text';
        } = {}
    ) {
        const { method = 'GET', responseType = 'json' } = options;
        const url = `${await this.baseUrl()}${endpoint}`;
        const { token } = await this.opts.identityApi.getCredentials();

        const res = await this.opts.fetchApi.fetch(url, {
            method,
            headers: { Authorization: `Bearer ${token ?? ''}` },
        });

        if (!res.ok) {
            throw new Error(`GitHub Actions proxy failed: ${res.status}`);
        }

        return responseType === 'json' ? await res.json() : await res.text();
    }

    // Minimal method used by the UI: list workflow runs for a repo
    async listWorkflowRuns(options: { owner: string; repo: string }) {
        const { owner, repo } = options;
        return this.makeRequest(`/repos/${owner}/${repo}/runs`);
    }
    async getWorkflow(options: { hostname?: string; owner: string; repo: string; id: number }) {
        const { owner, repo, id } = options;
        return this.makeRequest(`/repos/${owner}/${repo}/workflows/${id}`);
    }

    async getWorkflowRun(options: { hostname?: string; owner: string; repo: string; id: number }) {
        const { owner, repo, id } = options;
        return this.makeRequest(`/repos/${owner}/${repo}/runs/${id}`);
    }

    async reRunWorkflow(options: { hostname?: string; owner: string; repo: string; runId: number }) {
        const { owner, repo, runId } = options;
        return this.makeRequest(`/repos/${owner}/${repo}/runs/${runId}/rerun`, { method: 'POST' });
    }

    async listJobsForWorkflowRun(options: { hostname?: string; owner: string; repo: string; id: number; pageSize?: number; page?: number }) {
        const { owner, repo, id } = options;
        return this.makeRequest(`/repos/${owner}/${repo}/runs/${id}/jobs`);
    }

    async getWorkflowJobs(_options: { hostname?: string; owner: string; repo: string; runId: number }) {
        throw new Error('Not implemented');
    }

    async downloadJobLogsForWorkflowRun(options: { hostname?: string; owner: string; repo: string; runId: number }) {
        const { owner, repo, runId } = options;
        return this.makeRequest(`/repos/${owner}/${repo}/runs/${runId}/logs`, { responseType: 'text' });
    }

    async listBranches(_options: { hostname?: string; owner: string; repo: string; page?: number }) {
        // Skip branch fetching since branches are not displayed in the UI
        // This prevents unnecessary API calls that slow down component loading
        // const { owner, repo, page = 1 } = options;
        // return this.makeRequest(`/repos/${owner}/${repo}/branches?page=${page}`);
        return [];
    }

    async getDefaultBranch(options: { hostname?: string; owner: string; repo: string }) {
        const { owner, repo } = options;
        const data = await this.makeRequest(`/repos/${owner}/${repo}`);
        return data.default_branch;
    }
}
export const githubActionsProxyDeps = {
    discoveryApi: discoveryApiRef,
    identityApi: identityApiRef,
    fetchApi: fetchApiRef,
};